import React, { useState } from 'react';
import './CyberpunkWorldMap.css';

// Import sample data
import { countries, shopLocations } from '../../data/geoData';
import { productCategoriesL1 } from '../../data/productData';

// Import the world map SVG
import worldMapSvg from '../../assets/images/world-map.svg';

const WorldHeatMap = () => {
  const [selectedMetric, setSelectedMetric] = useState('totalSales');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showShops, setShowShops] = useState(true);
  const [hoveredCountry, setHoveredCountry] = useState(null);

  // Backup data in case import fails
  const backupCountries = [
    { id: 'USA', name: 'United States', region: 'North America', totalSales: 12450000, growth: 8.2, shopCount: 342 },
    { id: 'CHN', name: 'China', region: 'Asia', totalSales: 8750000, growth: 12.3, shopCount: 287 },
    { id: 'JPN', name: 'Japan', region: 'Asia', totalSales: 6120000, growth: 0.9, shopCount: 231 },
    { id: 'DEU', name: 'Germany', region: 'Europe', totalSales: 5780000, growth: 1.9, shopCount: 213 }
  ];
  
  // Use imported data or fallback
  const countryData = (countries && countries.length > 0) ? countries : backupCountries;
  
  const getCyberpunkColorForValue = (value, max) => {
    const intensity = Math.min(value / max, 1);
    if (intensity === 0) return 'rgba(15, 23, 42, 0)';
    
    const r = Math.floor(15 + (0 - 15) * intensity);
    const g = Math.floor(23 + (191 - 23) * intensity);
    const b = Math.floor(42 + (255 - 42) * intensity);
    const alpha = 0.5 + (0.8 * intensity);
    
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };
  
  const getCyberpunkGlowForValue = (value, max) => {
    const intensity = Math.min(value / max, 1);
    if (intensity < 0.5) return 'none';
    
    const glowIntensity = (intensity - 0.5) * 2;
    return `0 0 ${10 + glowIntensity * 20}px rgba(0, 191, 255, ${0.3 + glowIntensity * 0.7})`;
  };
  
  // Get max value for scaling
  const maxSales = Math.max(...countries.map(country => country.totalSales));
  const maxGrowth = Math.max(...countries.map(country => country.growth));
  const maxShopCount = Math.max(...countries.map(country => country.shopCount));
  
  const getMaxForMetric = () => {
    switch(selectedMetric) {
      case 'totalSales': return maxSales;
      case 'growth': return maxGrowth;
      case 'shopCount': return maxShopCount;
      default: return maxSales;
    }
  };
  
  const getShopBubbleSize = (shop) => {
    return Math.max(3, Math.sqrt(shop.sales / 8000));
  };
  
  const getShopBubbleColor = (shop) => {
    const intensity = shop.sales / Math.max(...shopLocations.map(s => s.sales));
    return `rgba(0, 255, 157, ${0.4 + intensity * 0.6})`;
  };

  return (    <div className="cyber-card">
      <div className="mb-6">
        <div className="cyber-header-section">
          <h1 className="cyber-title">Global Sales Heat Map</h1>
          <div className="cyber-accent-line"></div>
          <p className="cyber-subtitle">
            Visualize sales performance across geographical regions with real-time cyberpunk analytics
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="cyber-control-group">
            <label className="cyber-label">METRIC ANALYSIS</label>
            <select 
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="cyber-select"
            >
              <option value="totalSales">TOTAL SALES (USD)</option>
              <option value="growth">GROWTH RATE (%)</option>
              <option value="shopCount">SHOP DENSITY</option>
            </select>
          </div>
          
          <div className="cyber-control-group">
            <label className="cyber-label">PRODUCT CATEGORY</label>
            <select 
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="cyber-select"
            >
              <option value="all">ALL CATEGORIES</option>
              {productCategoriesL1.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name.toUpperCase()}</option>
              ))}
            </select>
          </div>
          
          <div className="cyber-control-group">
            <label className="cyber-checkbox-container">
              <input
                type="checkbox"
                checked={showShops}
                onChange={() => setShowShops(!showShops)}
                className="cyber-checkbox"
              />
              <span className="cyber-checkbox-label">SHOW SHOP LOCATIONS</span>
              <div className="cyber-checkbox-indicator"></div>
            </label>
          </div>
        </div>
      </div>      
      <div className="cyber-map-container">
        <div className="cyber-map-background">
          <div className="cyber-grid-overlay"></div>
          
          <div className="world-map-background">
            <img 
              src={worldMapSvg} 
              alt="World Map" 
              className="world-map-image"
            />
          </div>
          
          <div className="density-overlay">
            {countries.map(country => {
              const value = country[selectedMetric] || 0;
              const maxValue = getMaxForMetric();
              return (
                <div
                  key={country.id}
                  className={`country-density-zone country-${country.name.toLowerCase().replace(/\s+/g, '-')}`}
                  style={{
                    background: getCyberpunkColorForValue(value, maxValue),
                    boxShadow: getCyberpunkGlowForValue(value, maxValue)
                  }}
                  onMouseEnter={() => setHoveredCountry(country)}
                  onMouseLeave={() => setHoveredCountry(null)}
                  title={`${country.name}: ${selectedMetric === 'totalSales' ? '$' + (value/1000000).toFixed(1) + 'M' : 
                                                selectedMetric === 'growth' ? value.toFixed(1) + '%' : 
                                                value + ' shops'}`}
                />
              );
            })}
          </div>
          
          {showShops && (
            <div className="shop-bubbles-overlay">
              {shopLocations.slice(0, 15).map(shop => (
                <div
                  key={shop.id}
                  className="shop-bubble-marker"
                  style={{
                    left: `${((shop.lng + 180) / 360) * 100}%`,
                    top: `${((90 - shop.lat) / 180) * 100}%`,
                    width: `${getShopBubbleSize(shop)}px`,
                    height: `${getShopBubbleSize(shop)}px`,
                    background: getShopBubbleColor(shop),
                    boxShadow: `0 0 ${getShopBubbleSize(shop)}px ${getShopBubbleColor(shop)}`
                  }}
                  title={`${shop.name}: $${(shop.sales/1000).toFixed(0)}K`}
                />
              ))}
            </div>
          )}
          
          {hoveredCountry && (
            <div className="cyber-country-tooltip">
              <div className="cyber-tooltip-title">{hoveredCountry.name}</div>
              <div className="cyber-tooltip-data">
                Region: {hoveredCountry.region}<br/>
                Sales: ${(hoveredCountry.totalSales / 1000000).toFixed(1)}M<br/>
                Growth: {hoveredCountry.growth >= 0 ? '+' : ''}{hoveredCountry.growth.toFixed(1)}%<br/>
                Shops: {hoveredCountry.shopCount}
              </div>
            </div>
          )}
          
          <div className="cyber-scanlines"></div>
        </div>
        
        <div className="cyber-legend">
          <div className="cyber-legend-title">
            {selectedMetric === 'totalSales' ? 'SALES (USD)' : 
             selectedMetric === 'growth' ? 'GROWTH RATE (%)' : 'SHOP DENSITY'}
          </div>
          <div className="cyber-legend-gradient-container">
            <div className="cyber-legend-gradient"></div>
          </div>
          <div className="cyber-legend-labels">
            <span className="cyber-legend-low">LOW</span>
            <span className="cyber-legend-high">HIGH</span>
          </div>
        </div>
      </div>      
      <div className="mt-8">
        <div className="cyber-section-header">
          <h2 className="cyber-section-title">COUNTRIES PERFORMANCE DATA</h2>
          <div className="cyber-accent-line"></div>
        </div>
        
        <div className="cyber-table-container">
          <table className="cyber-table">
            <thead>
              <tr>
                <th className="cyber-th">COUNTRY</th>
                <th className="cyber-th">REGION</th>
                <th className="cyber-th">TOTAL SALES</th>
                <th className="cyber-th">GROWTH</th>
                <th className="cyber-th">SHOP COUNT</th>
              </tr>
            </thead>
            <tbody>
              <tr className="cyber-tr">
                <td className="cyber-td">
                  <div className="cyber-country-name">
                    <div className="cyber-flag-indicator"></div>
                    USA Test
                  </div>
                </td>
                <td className="cyber-td">North America</td>
                <td className="cyber-td">
                  <span className="cyber-value">$12.45M</span>
                </td>
                <td className="cyber-td">
                  <span className="cyber-growth cyber-growth-positive">
                    ↗ 8.2%
                  </span>
                </td>
                <td className="cyber-td">
                  <span className="cyber-shop-count">342</span>
                </td>
              </tr>
              
              {countryData.sort((a, b) => b[selectedMetric] - a[selectedMetric]).map((country, index) => (
                <tr key={country.id} className="cyber-tr" style={{animationDelay: `${index * 50}ms`}}>
                  <td className="cyber-td">
                    <div className="cyber-country-name">
                      <div className="cyber-flag-indicator"></div>
                      {country.name}
                    </div>
                  </td>
                  <td className="cyber-td">{country.region}</td>
                  <td className="cyber-td">
                    <span className="cyber-value">${(country.totalSales / 1000000).toFixed(2)}M</span>
                  </td>
                  <td className="cyber-td">
                    <span className={`cyber-growth ${country.growth >= 0 ? 'cyber-growth-positive' : 'cyber-growth-negative'}`}>
                      {country.growth >= 0 ? '↗' : '↘'} {Math.abs(country.growth).toFixed(1)}%
                    </span>
                  </td>
                  <td className="cyber-td">
                    <span className="cyber-shop-count">{country.shopCount}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>      
      {showShops && (
        <div className="mt-8">
          <div className="cyber-section-header">
            <h2 className="cyber-section-title">TOP SHOP LOCATIONS</h2>
            <div className="cyber-accent-line"></div>
          </div>
          
          <div className="cyber-table-container">
            <table className="cyber-table">
              <thead>
                <tr>
                  <th className="cyber-th">SHOP NAME</th>
                  <th className="cyber-th">LOCATION</th>
                  <th className="cyber-th">TYPE</th>
                  <th className="cyber-th">SALES</th>
                </tr>
              </thead>
              <tbody>
                {shopLocations
                  .sort((a, b) => b.sales - a.sales)
                  .slice(0, 10)
                  .map((shop, index) => (
                    <tr key={shop.id} className="cyber-tr" style={{animationDelay: `${index * 60}ms`}}>
                      <td className="cyber-td">
                        <div className="cyber-shop-name">{shop.name}</div>
                      </td>
                      <td className="cyber-td">
                        <span className="cyber-coordinates">
                          {shop.lat.toFixed(2)}°, {shop.lng.toFixed(2)}°
                        </span>
                      </td>
                      <td className="cyber-td">
                        <span className="cyber-shop-type">{shop.shopType}</span>
                      </td>
                      <td className="cyber-td">
                        <span className="cyber-value">${(shop.sales / 1000).toFixed(0)}K</span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorldHeatMap;